import { MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES } from '@/data/constants';
import type { TripPurpose } from '@/data/models/trip-purpose.model';

/** Most recently used reasons shown above the full list. */
export const REASON_FOR_TRIP_RECENT_LIMIT = 3;

/** The list only narrows once the user has typed this many characters. */
export const REASON_FOR_TRIP_MIN_SEARCH_LENGTH = 2;

export type ReasonForTripGroup = 'recent' | 'all';

export type ReasonForTripOption = TripPurpose & {
  group: ReasonForTripGroup;
};

/** Purpose codes the member can book, alphabetised, portal exclusions applied. */
export function getAvailablePurposeCodes(
  purposeCodes: TripPurpose[],
  isMemberPortal: boolean,
): TripPurpose[] {
  const allowed = isMemberPortal
    ? purposeCodes.filter(
        (purpose) =>
          !MEMBER_PORTAL_EXCLUDED_PURPOSE_NAMES.includes(
            purpose.name?.toLowerCase() || '',
          ),
      )
    : purposeCodes;

  return [...allowed].sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    return nameA.localeCompare(nameB);
  });
}

/**
 * Recents first (most recent first), then everything else alphabetically.
 *
 * The array is ordered by group because MUI's `groupBy` only renders correct
 * headers when options of the same group are contiguous. Recents are dropped
 * from the "All reasons" group rather than repeated, and are always intersected
 * with what the member can actually book.
 */
export function buildReasonForTripOptions(
  purposeCodes: TripPurpose[],
  recentPurposeCodeUUIDs: string[],
  isMemberPortal: boolean,
): ReasonForTripOption[] {
  const available = getAvailablePurposeCodes(purposeCodes, isMemberPortal);

  const recent: ReasonForTripOption[] = [];
  for (const uuid of recentPurposeCodeUUIDs) {
    if (recent.length >= REASON_FOR_TRIP_RECENT_LIMIT) break;
    if (recent.some((option) => option.purposeCodeUUID === uuid)) continue;

    const purpose = available.find((p) => p.purposeCodeUUID === uuid);
    if (purpose) {
      recent.push({ ...purpose, group: 'recent' });
    }
  }

  const rest: ReasonForTripOption[] = available
    .filter(
      (purpose) =>
        !recent.some((r) => r.purposeCodeUUID === purpose.purposeCodeUUID),
    )
    .map((purpose) => ({ ...purpose, group: 'all' as const }));

  return [...recent, ...rest];
}

/**
 * Case-insensitive "contains" match, applied only once the search text reaches
 * {@link REASON_FOR_TRIP_MIN_SEARCH_LENGTH}. Below that the full grouped list
 * stays visible.
 */
export function filterReasonForTripOptions(
  options: ReasonForTripOption[],
  searchText: string,
): ReasonForTripOption[] {
  const search = searchText.trim().toLowerCase();
  if (search.length < REASON_FOR_TRIP_MIN_SEARCH_LENGTH) {
    return options;
  }

  return options.filter((option) =>
    (option.name?.toLowerCase() || '').includes(search),
  );
}
